import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Odds } from '@frontend/sports/odds/feature';
import { RewardPoints } from '@frontend/sports/types/models/loyalty';
import { Decimal } from 'decimal.js';

import { NumpadAction } from '../../../lib/numpad/model';
import { PickId } from '../../core/picks/pick-id';
import { SlipResult } from '../betplacement/models';
import { BetPlacementError } from '../validation/errors/bet-placement-error';
import { BetslipError } from '../validation/errors/betslip-error';

export enum QuickBetActionButtonState {
    Login = 'Login',
    MakeDeposit = 'MakeDeposit',
    Place = 'Place',
    PlaceFreeBet = 'PlaceFreeBet',
    Placing = 'Placing',
    PlacingFreeBet = 'PlacingFreeBet',
    ProcessingDeposit = 'ProcessingDeposit',
    ProcessingDepositAndBet = 'ProcessingDepositAndBet',
    AcceptChanges = 'AcceptChanges',
    AcceptAndPlace = 'AcceptAndPlace',
    AcceptAndPlaceFreeBet = 'AcceptAndPlaceFreeBet',
    Deposit = 'Deposit',
    DepositAndPlaceBet = 'DepositAndPlaceBet',
    UpdateMinStake = 'UpdateMinStake',
}

export enum QuickBetUiState {
    None = 'None',
    Loading = 'Loading',
    Place = 'Place',
    Success = 'Success',
}

export interface IQuickBetActionButtonState {
    isDisabled: boolean;
    isProcessing: boolean;
    state: QuickBetActionButtonState;
    hasTaxation: boolean;
    possibleWinnings: Nullable<Decimal>;
    winningsBoost: Nullable<Decimal>;
    riskFreeAmount: Nullable<Decimal>;
    taxationAmount: Nullable<Decimal>;
    taxationLiability: Nullable<Decimal>;
    possibleWinningsNet: Nullable<Decimal>;
}

export interface IQuickBetErrorsStatus {
    isLocked: boolean;
    isClosed: boolean;
    oddsChanged: boolean;
    oddsAccepted: boolean;
    hasBetslipErrors: boolean;
    hasStakeUpdateErrors: boolean;
}

export interface IQuickBetWinningsState {
    hasTaxation: boolean;
    possibleWinnings: Nullable<Decimal>;
    winningsBoost: Nullable<Decimal>;
    riskFreeAmount: Nullable<Decimal>;
    taxationAmount: Nullable<Decimal>;
    taxationLiability: Nullable<Decimal>;
    taxationRate: Nullable<Decimal>;
    possibleWinningsNet: Nullable<Decimal>;
    originalWinnings?: Nullable<Decimal>;
}

export const defaultQuickBetActionButtonState: IQuickBetActionButtonState = {
    isDisabled: false,
    isProcessing: false,
    state: QuickBetActionButtonState.Login,
    hasTaxation: false,
    possibleWinnings: null,
    winningsBoost: null,
    riskFreeAmount: null,
    taxationAmount: null,
    taxationLiability: null,
    possibleWinningsNet: null,
};

export interface IQuickBetStakeState {
    stake: Nullable<string>; // numpad-keys.component work with strings
    numpadAction?: NumpadAction;
}

export const defaultQuickBetStakeState: IQuickBetStakeState = {
    stake: null,
};

export interface IQuickBetPlaceResultWarning {
    disableButton: boolean;
    error: BetPlacementError;
}

export interface IQuickBetPlaceResultSummaryState {
    stake: number;
    numberOfBets: number;
    currency?: string;
    possibleWinningsGross: number;
    possibleWinningsNet: number;
}

export interface IQuickBetPlaceResultState {
    betNumber: string;
    slipResults: SlipResult;
    isSuccessful: boolean;
    errors: BetslipError[];
    warnings: IQuickBetPlaceResultWarning[];
    placedNewCustomerOffer: boolean;
    summary: IQuickBetPlaceResultSummaryState;
    isEachWay: boolean;
    acceptedOdds: Odds | null;
    boostedOdds: Odds | null;
    rewardPoints?: RewardPoints;
}

export const defaultQuickBetPlaceResultState: IQuickBetPlaceResultState = {
    betNumber: '',
    slipResults: <SlipResult>{},
    isSuccessful: false,
    errors: [],
    warnings: [],
    placedNewCustomerOffer: false,
    summary: <IQuickBetPlaceResultSummaryState>{},
    isEachWay: false,
    acceptedOdds: null,
    boostedOdds: null,
};

export interface IQuickBetHelpState {
    isVisible: boolean;
    wasShown: boolean;
}

export const defaultQuickBetHelpState: IQuickBetHelpState = {
    isVisible: false,
    wasShown: false,
};

export interface IQuickBetState {
    uiState: QuickBetUiState;
    isDisabled: boolean;
    keepOpen: boolean;
    pickIds: PickId[];
    oddsChangedOnBetPlacement: boolean;
    pickLockedOnBetPlacement: boolean;
    displayHelper: QuickBetDisplayHelper;
    hasClosedPick: boolean;
    hasLockedPick: boolean;
    helpState: IQuickBetHelpState;
    stakeState: IQuickBetStakeState;
    actionButtonState: IQuickBetActionButtonState;
    betPlacedResult: IQuickBetPlaceResultState;
    inOverlay: boolean;
    inEditBetMode: boolean;
}

export interface QuickBetDisplayHelper {
    addedToBetslip: boolean;
    //hidden => QB can be reopend if the user correct the error from betbar
    hiddenByError: boolean;
    //the user did not take care of the error we are closing QB
    closedByError: boolean;
}

export const defaultQuickBetDisplayHelper: QuickBetDisplayHelper = {
    addedToBetslip: false,
    closedByError: false,
    hiddenByError: false,
};

export const defaultQuickBetState: IQuickBetState = {
    uiState: QuickBetUiState.None,
    isDisabled: false,
    keepOpen: false,
    pickIds: [],
    displayHelper: defaultQuickBetDisplayHelper,
    oddsChangedOnBetPlacement: false,
    pickLockedOnBetPlacement: false,
    hasClosedPick: false,
    hasLockedPick: false,
    helpState: defaultQuickBetHelpState,
    stakeState: defaultQuickBetStakeState,
    actionButtonState: defaultQuickBetActionButtonState,
    betPlacedResult: defaultQuickBetPlaceResultState,
    inOverlay: false,
    inEditBetMode: false,
};

export interface QuickBetStore {
    helpShown: boolean;
}

export interface IQuickBetPlaceResultStorageState {
    betNumber: string;
    isSuccessful: boolean;
    errors: BetslipError[];
    warnings: IQuickBetPlaceResultWarning[];
    placedNewCustomerOffer: boolean;
    summary: IQuickBetPlaceResultSummaryState;
    isEachWay: boolean;
    acceptedOdds: Odds | null;
    boostedOdds: Odds | null;
}

export interface IQuickBetSaveState {
    pickIds: string[];
    helpState: IQuickBetHelpState;
    keepOpen: boolean;
    stakeState: IQuickBetStakeState;
    actionButtonState: IQuickBetActionButtonState;
    betPlacedResult: IQuickBetPlaceResultStorageState;
}
